import { Box, Typography, alpha } from '@mui/material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import type { NavigateFunction } from 'react-router-dom'
import { SURAHS } from '../lib/surahs'
import { SURAH_NAMES_FR } from '../lib/surahNamesFr'
import { buildSurahPath } from '../lib/seo'
import type { SupportedLang } from '../lib/seo'
import { isPrerendering } from '../lib/prerender'

interface SurahIndexListProps {
  /** Which language's canonical surah URLs to link to. */
  lang: SupportedLang
}

/**
 * Progressive-enhancement click handler backing rule 1 on `SurahIndexList`
 * below. Intercepts ONLY a plain, unmodified left-click and redirects it
 * into the interactive Reader; every other interaction — ctrl/cmd/shift
 * /alt-click, middle-click (button !== 0), or a link given its own
 * `target` — falls straight through to the untouched `href`, exactly as
 * React Router's own `<Link>` would treat those same clicks. Crawlers
 * never fire `click` at all, so none of this is reachable from a crawl.
 *
 * Also inert during the SEO prerender snapshot: Puppeteer never clicks
 * anything, but gating on `isPrerendering()` keeps this handler provably
 * harmless if that ever changes, matching every other prerender guard in
 * this codebase.
 */
function handleSurahClick(
  event: React.MouseEvent<HTMLAnchorElement>,
  navigate: NavigateFunction,
  surahNumber: number
) {
  if (isPrerendering()) return
  if (event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  if (event.currentTarget.target) return
  event.preventDefault()
  navigate(`/reader/${surahNumber}`)
}

/**
 * The site's crawl hub: one real `<a href>` per surah, pointing at the
 * canonical `/<lang>/surah/<n>/<slug>` URL.
 *
 * This exists because internal links are the only thing that made Google
 * crawl the 228 prerendered surah pages. Before it, the home page emitted
 * exactly two anchors ("/" and "/auth") and the surah pages formed a
 * 1→114 prev/next chain with no entry point, so 185 URLs sat in Search
 * Console as "Discovered - currently not indexed": known from sitemap.xml,
 * never crawled, because nothing linked to them.
 *
 * Two rules this component must keep:
 *
 *   1. The crawlable `href` must always be the canonical surah URL
 *      (`RouterLink to={buildSurahPath(...)}`). A progressive-enhancement
 *      `onClick` (see `handleSurahClick` above) MAY redirect a plain,
 *      unmodified human left-click into the interactive Reader instead —
 *      that is additive UX layered on the link, never a replacement for
 *      it, and it must never remove the anchor or change what `href`
 *      resolves to. A crawler reads `href` and never fires a `click`
 *      event, so crawlers are completely unaffected; ctrl/cmd-click,
 *      middle-click, and right-click-to-copy all still follow the real,
 *      unmodified `href` too — though keyboard/screen-reader Enter
 *      activation dispatches a plain click and IS intercepted like any
 *      other unmodified left-click (see `handleSurahClick` above).
 *      (This is the SEO/Reader-unification amendment:
 *      a human now reaches the full interactive Reader in one click,
 *      while the exact same URL keeps doing its crawl-hub job for
 *      everyone and everything else that isn't a plain human click.)
 *   2. Link to the canonical surah URL, never `/reader/<n>` in `href`.
 *      robots.txt Disallows `/reader`, so a crawlable link there is a
 *      crawl dead end.
 *
 * Rendered by both SurahIndexPage (the `/<lang>/surah` hub) and the home
 * page section, so there is a single surah index to maintain.
 */
export function SurahIndexList({ lang }: SurahIndexListProps) {
  const navigate = useNavigate()
  return (
    <Box
      component="ul"
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: 'repeat(3, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
        },
        gap: 1,
      }}
    >
      {SURAHS.map((surah) => {
        const meaning =
          lang === 'fr' ? SURAH_NAMES_FR[surah.number] ?? surah.englishName : surah.englishName
        return (
          <Box component="li" key={surah.number} sx={{ minWidth: 0 }}>
            <Box
              component={RouterLink}
              to={buildSurahPath(lang, surah.number)}
              onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
                handleSurahClick(event, navigate, surah.number)
              }
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 0.75,
                px: 1.25,
                py: 1,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                textDecoration: 'none',
                color: 'text.primary',
                transition: 'background-color 120ms, border-color 120ms',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              <Typography
                component="span"
                sx={{ color: 'text.disabled', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}
              >
                {surah.number}.
              </Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="span"
                  sx={{
                    display: 'block',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {surah.name}
                </Typography>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {meaning}
                </Typography>
              </Box>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
